import type { Database, ContrailConfig, RecordRow, ProfileConfig } from "../types";
import { recordsTableName, normalizeProfileConfig } from "../types";
import { resolveIdentities } from "../identity";
import { getPDS } from "../client";
import { createIngestEvent, ingestRecords, recordTimeUs } from "../ingest";
import type { Did } from "@atcute/lexicons";
import { batchedInQuery } from "./helpers";

export interface ProfileEntry {
  did: string;
  handle: string | null;
  uri?: string;
  cid?: string | null;
  value?: unknown;
  collection?: string;
  rkey?: string;
  /** Hydrated by the labels module when the caller has accepted-labelers
   *  active and there are matching labels on this DID. */
  labels?: unknown;
}

export function collectDids(
  records: RecordRow[],
  hydrates: Record<string, Record<string, any[] | Record<string, any[]>>>
): string[] {
  const dids = new Set(records.map((r) => r.did));
  for (const rels of Object.values(hydrates)) {
    for (const value of Object.values(rels)) {
      const items = Array.isArray(value)
        ? value
        : Object.values(value).flat();
      for (const item of items) {
        if (item.did) dids.add(item.did);
      }
    }
  }
  return [...dids];
}

export async function resolveProfiles(
  db: Database,
  config: ContrailConfig,
  dids: string[]
): Promise<Record<string, ProfileEntry[]>> {
  if (dids.length === 0 || !config.profiles || config.profiles.length === 0) {
    return {};
  }

  const profileConfigs = config.profiles.map(normalizeProfileConfig);
  const result: Record<string, ProfileEntry[]> = {};
  const indexedUris = new Set<string>();

  // Batch-lookup profile records for each configured profile collection
  for (const pc of profileConfigs) {
    const { collection, rkey: configRkey, shortName } = pc;
    const rkey = configRkey ?? "self";
    const table = recordsTableName(shortName ?? collection);
    const uris = dids.map((did) => `at://${did}/${collection}/${rkey}`);

    const rows = await batchedInQuery<Omit<RecordRow, "collection">>(
      db,
      `SELECT uri, did, rkey, cid, record FROM ${table} WHERE uri IN (__IN__)`,
      [],
      uris
    );

    for (const row of rows) {
      indexedUris.add(row.uri);
      let value: unknown = null;
      if (row.record) {
        try {
          value = JSON.parse(row.record);
        } catch {
          value = row.record;
        }
      }
      if (!result[row.did]) result[row.did] = [];
      result[row.did].push({
        did: row.did,
        handle: null, // filled below
        uri: row.uri,
        collection,
        rkey: row.rkey,
        cid: row.cid,
        value,
      });
    }
  }

  // Resolve identities for all DIDs
  const identities = await resolveIdentities(db, dids, config);

  // Fetch each missing (DID, profile collection) independently. One cached
  // profile collection must not make another configured collection look done.
  const missingProfiles = dids.flatMap((did) =>
    profileConfigs.flatMap((profile) => {
      const rkey = profile.rkey ?? "self";
      const uri = `at://${did}/${profile.collection}/${rkey}`;
      return indexedUris.has(uri) ? [] : [{ did, profile }];
    }),
  );
  if (missingProfiles.length > 0) {
    const fetched = await fetchMissingProfiles(db, config, missingProfiles);
    for (const [did, entries] of Object.entries(fetched)) {
      if (!result[did]) result[did] = [];
      result[did].push(...entries);
    }
  }

  // Fill in handles and create entries for DIDs without profile records
  for (const did of dids) {
    const identity = identities.get(did);
    const handle = identity?.handle ?? null;

    if (result[did]) {
      for (const entry of result[did]) {
        entry.handle = handle;
      }
    } else {
      result[did] = [{ did, handle }];
    }
  }

  return result;
}

/**
 * Fetch profile records from PDS for DIDs not yet in the index.
 * Fetches in parallel across all configured profile collections,
 * indexes the results into D1 for future requests.
 */
async function fetchMissingProfiles(
  db: Database,
  config: ContrailConfig,
  missing: Array<{ did: string; profile: ProfileConfig }>,
): Promise<Record<string, ProfileEntry[]>> {
  const fetched = await Promise.all(
    missing.map(async ({ did, profile }) => {
      const { collection, rkey: configRkey } = profile;
      const rkey = configRkey ?? "self";
      const uri = `at://${did}/${collection}/${rkey}`;
      try {
        const pds = await getPDS(did as Did, db, config);
        if (!pds) return null;

        const url = new URL("/xrpc/com.atproto.repo.getRecord", pds);
        url.searchParams.set("repo", did);
        url.searchParams.set("collection", collection);
        url.searchParams.set("rkey", rkey);

        const response = await fetch(url.toString());
        if (!response.ok) return null;
        const data = (await response.json()) as {
          uri?: string;
          value?: unknown;
          cid?: string;
        };
        if (!data.value || !data.cid || (data.uri && data.uri !== uri)) {
          return null;
        }

        const nowUs = Date.now() * 1000;
        return createIngestEvent({
          uri,
          did,
          collection,
          rkey,
          operation: "create",
          cid: data.cid,
          value: data.value,
          timeUs: recordTimeUs(data.value, collection, config, nowUs),
          indexedAt: nowUs,
          source: {
            id: "pds-profile",
            time_us: nowUs,
            revision: null,
            cursor: null,
          },
        });
      } catch {
        return null;
      }
    }),
  );

  const events = fetched.filter((event) => event !== null);
  if (events.length === 0) return {};
  const { accepted } = await ingestRecords(db, events, config, {
    phase: "historical",
  });
  const result: Record<string, ProfileEntry[]> = {};
  for (const event of accepted) {
    if (event.operation === "delete") continue;
    if (!result[event.did]) result[event.did] = [];
    result[event.did].push({
      did: event.did,
      handle: null,
      uri: event.uri,
      collection: event.collection,
      rkey: event.rkey,
      cid: event.cid,
      value: event.record ? JSON.parse(event.record) : null,
    });
  }
  return result;
}
